/**
 * touchpoints — the 26747 touchpoint vocabulary, mirrored client-side from
 * the substrate's `tools/touchpoints.py` (the substrate is authoritative —
 * see tasks/overnight-2026-09-09-plan.md, "Decisions carried into the plan":
 * "Touchpoint kinds are defined ONCE in the substrate; planned/exception is
 * derived from kind, never stored"). Keep `TOUCHPOINT_KINDS` byte-for-byte in
 * sync with the substrate's own list — a future cross-repo contract test
 * (modelled on `tests/contract/test_notification_states_sync.py`) is the
 * intended guard once both sides exist; until then, this is the single
 * client-side copy every touchpoint emitter imports.
 */

/** A plain string-array literal (not a TS enum) — `as const` gives the exact
 *  same "array + union type" shape `notificationStates.ts` (WT-P1) uses, so a
 *  future cross-repo sync test can diff this array directly against the
 *  substrate's list without reflection tricks. */
export const TOUCHPOINT_KINDS = [
  // Planned — the ordinary path a run is expected to take.
  "brief_review",
  "brief_launch",
  "hitl_answer",
  "result_review",
  "reply_send",
  // Exception — something needed a human beyond the ordinary path.
  "correction",
  "rework",
  "manual_takeover",
  "escalation",
  "clarification",
] as const;

export type TouchpointKind = (typeof TOUCHPOINT_KINDS)[number];

/** The ordinary path. Mirrors the substrate's PLANNED_KINDS. */
export const PLANNED_KINDS: readonly TouchpointKind[] = [
  "brief_review",
  "brief_launch",
  "hitl_answer",
  "result_review",
  "reply_send",
];

/** Something needed a human beyond the ordinary path. Mirrors the
 *  substrate's EXCEPTION_KINDS. `planned`/`exception` is always DERIVED from
 *  kind via these two arrays — never stored as its own field, per the
 *  ratified decision above. */
export const EXCEPTION_KINDS: readonly TouchpointKind[] = [
  "correction",
  "rework",
  "manual_takeover",
  "escalation",
  "clarification",
];

/** POST /api/workflows/telemetry/touchpoints — the ora resource route every
 *  ora `TouchpointProvider` mount posts to (see `api.workflows.telemetry.touchpoints.tsx`). */
export const TOUCHPOINTS_POST_URL = "/api/workflows/telemetry/touchpoints";

export type TouchpointPhase = "arrive" | "leave";
export type TouchpointSubjectType = "run" | "brief";
export type TouchpointSurface = "outlook_addin" | "ora_web";

/**
 * The wire shape `record_touchpoints` expects — see the plan's Touchpoints
 * tool contract. Every field beyond `kind`/`phase`/`subject_*`/`surface` is an
 * IDENTIFIER, never free text or an address: `mailbox_key` is the one field
 * allowed to look like a mailbox, everything else is validated (dev mode
 * only, in `useTouchpoint`) to contain no `@` and stay under 128 chars.
 */
export interface TouchpointEvent {
  event_key: string;
  kind: TouchpointKind;
  phase: TouchpointPhase;
  subject_type: TouchpointSubjectType;
  subject_id: string;
  surface: TouchpointSurface;
  /** ISO 8601. */
  occurred_at: string;
  session_key?: string;
  mailbox_key?: string;
  client_version?: string;
}

/** `true` for a kind in `PLANNED_KINDS`. */
export function isPlannedTouchpoint(kind: TouchpointKind): boolean {
  return PLANNED_KINDS.includes(kind);
}

/** `true` for a kind in `EXCEPTION_KINDS`. */
export function isExceptionTouchpoint(kind: TouchpointKind): boolean {
  return EXCEPTION_KINDS.includes(kind);
}
